import type AhaEditor from '../../AhaEditor';
import { type CellCoordinates } from './TableStructure';
import type TableStructure from './TableStructure';
import { getTableStructureFromElement } from './TableStructure';
import TableUtils from './TableUtils';
import { Editor, Transforms } from 'slate';

const KEY_DIRECTION = {
  ArrowUp: 'U',
  ArrowRight: 'R',
  ArrowDown: 'D',
  ArrowLeft: 'L',
};

const DIRECTION_DELTA = {
  U: { x: 0, y: -1 },
  R: { x: 1, y: 0 },
  D: { x: 0, y: 1 },
  L: { x: -1, y: 0 },
};

// Return true if the coordinates are outside of the table
const outOfBounds = (
  tableStructure: TableStructure,
  coords: CellCoordinates
) => {
  return (
    coords.rowIndex < 0 ||
    coords.rowIndex >= tableStructure.height ||
    coords.columnIndex < 0 ||
    coords.columnIndex >= tableStructure.width
  );
};

// Given a direction and coordinates, return the next cell's coordinates
// if the direction is followed.
// The selection should flip to the previous/next row if we're at
// the start/end of the current row.
// If it's not possible, return undefined.
const getNextCell = (
  tableStructure: TableStructure,
  coords: CellCoordinates,
  direction: 'U' | 'R' | 'D' | 'L'
) => {
  const nextCellPos: CellCoordinates = {
    columnIndex: -1,
    rowIndex: -1,
  };

  if (
    direction === 'R' &&
    coords.columnIndex === tableStructure.width - 1 &&
    coords.rowIndex < tableStructure.height - 1
  ) {
    nextCellPos.columnIndex = 0;
    nextCellPos.rowIndex = coords.rowIndex + 1;
  } else if (
    direction === 'L' &&
    coords.columnIndex === 0 &&
    coords.rowIndex > 0
  ) {
    nextCellPos.columnIndex = tableStructure.width - 1;
    nextCellPos.rowIndex = coords.rowIndex - 1;
  } else {
    const delta = DIRECTION_DELTA[direction];
    nextCellPos.columnIndex = coords.columnIndex + delta.x;
    nextCellPos.rowIndex = coords.rowIndex + delta.y;
  }

  if (outOfBounds(tableStructure, nextCellPos)) return;

  return tableStructure.cell(nextCellPos.columnIndex, nextCellPos.rowIndex);
};

const handleShiftSelectCells = (
  ahaEditor: AhaEditor,
  event: KeyboardEvent
): boolean => {
  const { editor, controller } = ahaEditor;
  if (!editor.selection) return false;

  const { tableSelection } = controller.state.pluginState;
  if (!tableSelection) return false;

  const table = TableUtils.currentTable(editor);
  if (!table) return false;

  const tableStructure = getTableStructureFromElement(editor, table[0]);

  const currentFocusCell = TableUtils.parentCell(
    editor,
    editor.selection.focus.path
  );
  if (!currentFocusCell) return false;

  let focusCellCoords: CellCoordinates | undefined;

  if (!tableSelection.multipleCellsSelected) {
    // If moving left or right, we don't want to interfere with normal
    // text selection, so we only handle the case where the focus is at the
    // start/end edge of the cell, depending on direction.
    const isLeftEdge =
      event.key === 'ArrowLeft' &&
      Editor.isStart(editor, editor.selection.focus, currentFocusCell[1]);
    const isRightEdge =
      event.key === 'ArrowRight' &&
      Editor.isEnd(editor, editor.selection.focus, currentFocusCell[1]);

    if (isLeftEdge || isRightEdge) {
      focusCellCoords = tableStructure.pathToCoordinates(
        currentFocusCell[1].slice(-2)
      );
    }
  } else {
    focusCellCoords = tableSelection.tableRange?.focusPosition;
  }

  // If we don't have a focus cell at this point, don't handle the event.
  if (!focusCellCoords) return false;

  const nextCell = getNextCell(
    tableStructure,
    focusCellCoords,
    KEY_DIRECTION[event.key]
  );
  if (!nextCell) {
    // If there isn't a next cell, it's likely that the selection
    // is going to be outside the table.

    // We might want to extend the selection to the next/previous block.
    const shouldMoveToPrevious =
      focusCellCoords.rowIndex === 0 &&
      ((event.key === 'ArrowUp' && !tableSelection.multipleCellsSelected) ||
        (event.key === 'ArrowLeft' && focusCellCoords.columnIndex === 0));
    const shouldMoveToNext =
      focusCellCoords.rowIndex === tableStructure.height - 1 &&
      ((event.key === 'ArrowDown' && !tableSelection.multipleCellsSelected) ||
        (event.key === 'ArrowRight' &&
          focusCellCoords.columnIndex === tableStructure.width - 1));

    if (shouldMoveToPrevious) {
      const point = Editor.before(editor, Editor.start(editor, table[1]));
      if (point) {
        Transforms.setSelection(editor, {
          focus: point,
        });
      }
    }

    if (shouldMoveToNext) {
      const point = Editor.after(editor, Editor.end(editor, table[1]));
      if (point) {
        Transforms.setSelection(editor, {
          focus: point,
        });
      }
    }

    event.preventDefault();
    return true;
  }

  // Set the selection to the next cell, prevent the default key behavior
  // and stop any further event handlers from being called by returning true.
  const cellPath = [...table[1], ...nextCell.relativePath];
  const startOfCell = Editor.start(editor, cellPath);

  Transforms.setSelection(editor, {
    focus: startOfCell,
  });

  event.preventDefault();
  return true;
};

export default handleShiftSelectCells;
